/**
 * PoPerformanceDrawer — Delivery/quality/cost performance of a single PO,
 * accessible from any MCI row. Aggregates every receipt of every MCI line
 * that shares the same PO number and computes:
 *   • Ordered vs Delivered (Accepted + Pending + Rejected)
 *   • On-time delivery % (against MCI expectedDate)
 *   • Quality Yield % (Accepted ÷ Delivered)
 *   • Rejection rate
 *   • Cost variance (PO value vs delivered value)
 *   • Per-receipt timeline
 */
import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { X, TrendingUp, TrendingDown, CheckCircle2, AlertTriangle,
         Clock, DollarSign, Package } from 'lucide-react';

const C = { primary: '#1E4E8C', primaryText: 'var(--tone-navy-text)', accent: '#F26B1F', slate: 'var(--text-primary)',
             slateMuted: 'var(--text-muted)', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444', info: '#3B82F6' };

const fmt = (n, d = 0) => (n == null || Number.isNaN(n))
  ? '—' : (typeof n === 'number' ? n.toLocaleString(undefined, { maximumFractionDigits: d }) : n);

function Tile({ label, value, color, hint, icon: Icon, testId }) {
  return (
    <div className="p-2 rounded border bg-[var(--card-bg)]" style={{ borderColor: C.border }} data-testid={testId}>
      <div className="flex items-center gap-1 mb-1">
        {Icon && <Icon className="w-3 h-3" style={{ color }} />}
        <div className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>{label}</div>
      </div>
      <div className="text-base font-bold font-mono" style={{ color }}>{value}</div>
      {hint && <div className="text-[8.5px]" style={{ color: C.slateMuted }}>{hint}</div>}
    </div>
  );
}

export default function PoPerformanceDrawer({ projectId, mciLineNumber, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!mciLineNumber) return;
    (async () => {
      try {
        const { data: d } = await api.get(
          `/v1/projects/${projectId}/mtx/po-performance?mci=${encodeURIComponent(mciLineNumber)}`);
        setData(d);
      } catch { setData({ error: 'Failed to load' }); }
    })();
  }, [projectId, mciLineNumber]);
  if (!mciLineNumber) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" style={{ background: 'rgba(15,23,42,0.4)' }} onClick={onClose}>
      <div className="w-[560px] bg-[var(--card-bg)] shadow-2xl overflow-y-auto p-4" onClick={e => e.stopPropagation()}
        data-testid="po-performance-drawer">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" style={{ color: C.primaryText }} />
              <div className="text-sm font-bold" style={{ color: C.slate }}>PO Performance</div>
            </div>
            <div className="text-[10px]" style={{ color: C.slateMuted }}>
              {data?.poNumber ? <>PO <b style={{ color: C.accent }}>{data.poNumber}</b> · {data.vendor}</> : 'Loading…'}
            </div>
          </div>
          <button onClick={onClose} data-testid="po-perf-close"><X className="w-4 h-4" /></button>
        </div>
        {!data && <div className="text-[11px] italic" style={{ color: C.slateMuted }}>Loading PO performance…</div>}
        {data?.error && <div className="text-[11px]" style={{ color: C.danger }}>{data.error}</div>}
        {data && !data.error && (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <Tile testId="po-perf-ordered" label="Ordered Qty"       value={fmt(data.orderedQty)}      color={C.slate}   icon={Package} hint={`${data.currency} ${fmt(data.poValue, 0)}`} />
              <Tile testId="po-perf-delivered" label="Delivered"        value={fmt(data.deliveredQty)}    color={C.primary} icon={CheckCircle2} hint={`${fmt(data.deliveredPct, 1)}% of PO`} />
              <Tile testId="po-perf-yield"    label="Quality Yield"     value={`${fmt(data.qualityYieldPct, 1)}%`} color={data.qualityYieldPct >= 95 ? C.success : C.warn} icon={data.qualityYieldPct >= 95 ? TrendingUp : TrendingDown} hint={`${fmt(data.acceptedQty)} accepted`} />
              <Tile testId="po-perf-reject"   label="Rejection Rate"    value={`${fmt(data.rejectRatePct, 2)}%`} color={data.rejectRatePct > 3 ? C.danger : C.slate} icon={AlertTriangle} hint={`${fmt(data.rejectedQty)} rejected`} />
              <Tile testId="po-perf-ontime"   label="On-Time Delivery"  value={`${fmt(data.onTimePct, 1)}%`} color={data.onTimePct >= 90 ? C.success : C.warn} icon={Clock} hint={`${data.onTimeReceipts}/${data.receiptCount} receipts`} />
              <Tile testId="po-perf-costvar"  label="Cost Variance"     value={`${data.costVariancePct >= 0 ? '+' : ''}${fmt(data.costVariancePct, 1)}%`} color={data.costVariancePct <= 0 ? C.success : C.danger} icon={DollarSign} hint={`${data.currency} ${fmt(data.deliveredValue, 0)}`} />
            </div>

            {/* Verdict banner */}
            <div className="p-2 mb-3 rounded text-[10px] font-semibold border-l-4"
              style={{ background: data.verdict?.color === 'GREEN' ? 'var(--tone-green-bg)'
                                     : data.verdict?.color === 'AMBER' ? 'var(--tone-amber-bg)' : 'var(--tone-red-bg)',
                        borderColor: data.verdict?.color === 'GREEN' ? C.success
                                       : data.verdict?.color === 'AMBER' ? C.warn : C.danger,
                        color: data.verdict?.color === 'GREEN' ? 'var(--tone-green-text)'
                                : data.verdict?.color === 'AMBER' ? 'var(--tone-amber-text)' : 'var(--tone-red-text)' }}>
              Verdict: <b>{data.verdict?.rating}</b> — {data.verdict?.reason}
            </div>

            <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Receipt Timeline</div>
            <div className="rounded border overflow-x-auto" style={{ borderColor: C.border }}>
              <table className="w-full text-[10px]">
                <thead style={{ background: 'var(--card-bg-alt)' }}>
                  <tr>
                    {['Receipt', 'Date', 'Days vs ETA', 'GRN', 'Pending', 'Accepted', 'Rejected'].map(h => (
                      <th key={h} className="text-left p-1 uppercase" style={{ color: C.slateMuted, fontSize: 8 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data.receipts || []).map((r, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td className="p-1 font-mono" style={{ color: C.accent }}>{r.receiptNumber}</td>
                      <td className="p-1" style={{ color: C.slate }}>{(r.receiptDate || '').slice(0, 10)}</td>
                      <td className="p-1 font-mono" style={{ color: r.daysLate > 0 ? C.danger : C.success }}>
                        {r.daysLate == null ? '—' : (r.daysLate > 0 ? `+${r.daysLate}d late` : `${-r.daysLate}d early`)}
                      </td>
                      <td className="p-1 text-right font-mono" style={{ color: C.primaryText }}>{fmt(r.grnQty)}</td>
                      <td className="p-1 text-right font-mono" style={{ color: C.warn }}>{fmt(r.pendingQty)}</td>
                      <td className="p-1 text-right font-mono font-bold" style={{ color: C.success }}>{fmt(r.acceptedQty)}</td>
                      <td className="p-1 text-right font-mono" style={{ color: C.danger }}>{fmt(r.rejectedQty)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-[9px]" style={{ color: C.slateMuted }}>
              Covers {data.mciLineCount} MCI line(s) under PO {data.poNumber}.
              Delivered value = Accepted × per-unit PO value.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
